import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import Pagination from '../components/shared/Pagination';
import FilterBar, { dateRangeForFilter, DEFAULT_DATE_PRESETS } from '../components/shared/FilterBar';
import { useResizableColumns, ResizeHandle, useSortableData, SortableHeader } from '../components/shared/tableUtils';

const defaultInquiries = [
  {
    id: 1,
    inquiryId: 'INQ-001',
    mediator: 'Eric Anderson',
    requestedDates: 'Apr 15-17, 2026',
    requestingParty: 'Sarah Chen',
    email: 'Sarah@littler.com',
    firm: 'Littler',
    status: 'In Progress',
    dateSubmitted: 'Mar 10, 2026',
  },
  {
    id: 2,
    inquiryId: 'INQ-001',
    mediator: 'Lisa Rodriguez',
    requestedDates: 'Apr 20-22, 2026',
    requestingParty: 'James Liu',
    email: 'Liu@Morrison.com',
    firm: 'Morrison & Foerster',
    status: 'New',
    dateSubmitted: 'Mar 12, 2026',
  },
  {
    id: 3,
    inquiryId: 'INQ-001',
    mediator: 'Michael Torres',
    requestedDates: 'May 1-3, 2026',
    requestingParty: 'David Park',
    email: 'David@Baker.com',
    firm: 'Baker McKenzie',
    status: 'Converted',
    dateSubmitted: 'Mar 5, 2026',
  },
  {
    id: 4,
    inquiryId: 'INQ-001',
    mediator: 'Sarah Williams',
    requestedDates: 'Mar 28-30, 2026',
    requestingParty: 'Amy Nakamura',
    email: 'Amy@Traurig.com',
    firm: 'Greenberg Traurig',
    status: 'Not Converted',
    dateSubmitted: 'Feb 28, 2026',
  },
  {
    id: 5,
    inquiryId: 'INQ-001',
    mediator: 'Eric Anderson',
    requestedDates: 'May 10-12, 2026',
    requestingParty: 'Robert Kim',
    email: 'Kim@Latham.com',
    firm: 'Latham & Watkins',
    status: 'New',
    dateSubmitted: 'Mar 14, 2026',
  },
  {
    id: 6,
    inquiryId: 'INQ-001',
    mediator: 'Lisa Rodriguez',
    requestedDates: 'Apr 25, 2026',
    requestingParty: 'Karen Walsh',
    email: 'Karen@DLA.com',
    firm: 'DLA Piper',
    status: 'In Progress',
    dateSubmitted: 'Mar 10, 2026',
  },
];

// Format a {direction, percent, compareLabel} change object for the KPI
// badge \u2014 returns the shape buildKpiCards still uses (change/changeColor/
// bgColor/direction) plus compareLabel so the trailing "vs <X>" text
// moves with the active filter range. Falls back to "last month".
function changeBadge(change) {
  const dir = change?.direction || 'flat';
  const pct = change?.percent;
  const isNew = change?.isNew;
  const isDown = dir === 'down';
  let label;
  if (isNew) {
    label = `+${change.value} new`;
  } else if (pct == null) {
    label = '\u2014';
  } else {
    const sign = pct > 0 ? '+' : '';
    label = `${sign}${pct}%`;
  }
  return {
    change: label,
    changeColor: isDown ? '#a60003' : '#00a666',
    bgColor: isDown ? '#fff1f1' : '#e8fdf5',
    direction: isDown ? 'down' : 'up',
    compareLabel: change?.compareLabel || 'last month',
  };
}

function buildKpiCards(loading, totalEntries, statusCounts) {
  // While the stats fetch is in flight, show '\u2014'. Once it resolves and
  // the bucket is genuinely empty (no inquiries with that status), show
  // '0' instead of '\u2014' \u2014 '\u2014' implied "unknown", which was wrong.
  const cell = (n) => (loading ? '\u2014' : String(n || 0));
  const ch = statusCounts?.change || {};
  // Conversion Rate tile = Converted / Total. BE computes the rate +
  // its MoM change; FE renders as "NN%".
  const rateCell = loading
    ? '\u2014'
    : `${statusCounts?.conversionRate || 0}%`;
  return [
    {
      label: 'Total Inquiries',
      value: loading ? '\u2014' : String(totalEntries || 0),
      ...changeBadge(ch.total),
    },
    {
      label: 'In Progress',
      value: cell(statusCounts?.['In Progress']),
      ...changeBadge(ch['In Progress']),
    },
    {
      label: 'Converted',
      value: cell(statusCounts?.Converted),
      ...changeBadge(ch.Converted),
    },
    {
      label: 'Conversion Rate',
      value: rateCell,
      ...changeBadge(ch.conversionRate),
    },
  ];
}

// follow_up_date is a bare DATE ("YYYY-MM-DD"). Format from local Y/M/D parts
// (NOT `new Date(iso)`, which parses as UTC midnight and drifts a day west of
// Greenwich). Returns '' for empty so the sort hook sinks it to the bottom.
function formatShortDatePart(value) {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d)).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/* ---------- The "On Hold" cell ----------
 *
 * Ported from the Scale mediator portal's inquiry list.
 *
 * The count leads, so the number survives even when the column is squeezed to
 * a sliver — a trailing "+3 more" is the first thing to be cut off, and it was
 * also swallowed by the row's own click handler, so clicking it navigated to
 * the detail page instead of telling you what the three dates were. The whole
 * thing is a real button that opens a list and stops the click reaching the
 * row.
 *
 * The preview is dates only. Marking half-days here made a scanning surface
 * harder to scan for a detail nobody needs at a glance; which half is spelled
 * out in words in the popover, where the decision is actually made.
 */
const SLOT_WORD = { morning: 'Morning', afternoon: 'Afternoon' };

function holdPreview(holds) {
  let lastMonth = null;
  return holds
    .map((h) => {
      const [y, m, d] = String(h.iso).split('-').map(Number);
      const key = `${y}-${m}`;
      const dt = new Date(y, m - 1, d);
      const head = key === lastMonth ? '' : `${dt.toLocaleDateString('en-US', { month: 'short' })} `;
      lastMonth = key;
      return `${head}${d}`;
    })
    .join(', ');
}

function HoldDatesCell({ holds = [], firm, open, onToggle, onClose, anchor, onOpenInquiry }) {
  // The panel, so the scroll listener below can tell the page moving under the
  // popover from the popover's own list being scrolled.
  const panelRef = React.useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    /* The panel is fixed-positioned against a rectangle measured once, so any
       scroll of the PAGE leaves it pointing at nothing — it closes instead of
       drifting. The listener has to be capture-phase to catch scrolls from
       inner containers, which means it also catches the popover's own list
       (it scrolls past ~6 dates); ignoring events that originate inside the
       panel is what stops reading a long list from dismissing it. */
    const onScroll = (e) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      onClose();
    };
    const close = () => onClose();
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose]);

  if (holds.length === 0) {
    return <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, color: '#C5CAD3' }}>—</span>;
  }

  // A single hold already says everything, so it gets no affordance — a control
  // that reveals nothing is the failure this is fixing.
  if (holds.length === 1) {
    const h = holds[0];
    const [y, m, d] = String(h.iso).split('-').map(Number);
    const label = new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return (
      <span className="truncate" style={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, fontWeight: 600, color: '#B45309' }}>
        {label}
        <span style={{ fontWeight: 400, color: '#a1741f' }}>
          {` · ${h.isFullDay ? 'Full Day' : SLOT_WORD[h.slot] || 'Half Day'}`}
        </span>
      </span>
    );
  }

  const panelW = 268;
  const left = anchor ? Math.min(Math.max(anchor.left, 10), window.innerWidth - panelW - 10) : 0;
  const flip = anchor ? anchor.bottom + 300 > window.innerHeight : false;
  let lastMonth = null;

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={!!open}
        onClick={(e) => {
          // Must not reach the row, which navigates to the detail page.
          e.stopPropagation();
          onToggle(e.currentTarget.getBoundingClientRect());
        }}
        className="inline-flex items-center min-w-0"
        style={{ gap: 6, border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 6px', margin: '0 -6px', borderRadius: 6, maxWidth: '100%' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#FDF6E9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ flex: 'none', fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: '#FBF1DD', color: '#a1741f', whiteSpace: 'nowrap' }}>
          {holds.length} dates
        </span>
        <span className="truncate min-w-0" style={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, fontWeight: 600, color: '#B45309' }}>
          {holdPreview(holds)}
        </span>
        <svg width="10" height="10" viewBox="0 0 14 14" fill="none" style={{ flex: 'none' }}>
          <path d="M3.5 5.25L7 8.75l3.5-3.5" stroke="#C08A2E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && anchor && createPortal(
        <>
          <div onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 2000 }} />
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Dates on hold"
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'fixed', left, top: flip ? Math.max(10, anchor.top - 300) : anchor.bottom + 6, width: panelW, zIndex: 2001, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 12px 32px rgba(16,24,40,0.18)', fontFamily: 'Inter, sans-serif', overflow: 'hidden' }}
          >
            <div className="flex items-center justify-between" style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#0A0F1C' }}>{holds.length} dates on hold</span>
              <button type="button" onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9CA3AF', lineHeight: 1 }}>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </button>
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto', padding: '6px 12px 10px' }}>
              {holds.map((h) => {
                const [y, m, d] = String(h.iso).split('-').map(Number);
                const dt = new Date(y, m - 1, d);
                const key = `${y}-${m}`;
                const head = key === lastMonth ? null : dt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
                lastMonth = key;
                return (
                  <div key={h.iso}>
                    {head && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '.04em', margin: '8px 0 4px' }}>{head}</div>
                    )}
                    <div className="flex items-center justify-between" style={{ gap: 8, padding: '3px 0' }}>
                      <span className="flex flex-col min-w-0">
                        <span style={{ fontSize: 12.5, color: '#374151' }}>
                          {dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        {/* How long the hold was meant to run. Nothing releases
                            on this date — it states intent. */}
                        {h.duration && (
                          <span style={{ fontSize: 11, color: '#9CA3AF' }} title={h.duration}>
                            Held for {h.duration}
                          </span>
                        )}
                      </span>
                      <span style={{ flex: 'none', background: '#f6e3bd', color: '#a1741f', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap' }}>
                        {h.isFullDay ? 'Full Day' : SLOT_WORD[h.slot] || 'Half Day'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between" style={{ padding: '9px 12px', borderTop: '1px solid #f3f4f6', gap: 8 }}>
              <span className="truncate" style={{ fontSize: 11.5, color: '#9CA3AF' }}>{firm ? `Held for ${firm}` : ''}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); onOpenInquiry(); }} style={{ flex: 'none', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#115e59' }}>
                Open inquiry →
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

function getStatusBadge(status) {
  // Three supported statuses. 'New' is deprecated — any legacy row that
  // sneaks through pre-migration renders with the In Progress styling.
  const map = {
    'In Progress': { text: '#92400e', bg: '#fef3c7' },
    'Converted': { text: '#115e59', bg: '#e8fdf5' },
    'Not Converted': { text: '#b91c1c', bg: '#fde8e9' },
  };
  const normalized = status === 'New' ? 'In Progress' : status;
  const style = map[normalized] || { text: '#5b5a64', bg: '#f3f4f6' };
  return (
    <span
      className="inline-flex items-center rounded-full px-[10px] py-[3px] text-[11px] font-medium"
      style={{ color: style.text, backgroundColor: style.bg }}
    >
      {normalized}
    </span>
  );
}

function TrendUpIcon({ color }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M1.5 8.5L4.5 5.5L6.5 7.5L10.5 3.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7.5 3.5H10.5V6.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function TrendDownIcon({ color }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M1.5 3.5L4.5 6.5L6.5 4.5L10.5 8.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7.5 8.5H10.5V5.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="6.417" cy="6.417" r="4.667" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 10L12.5 12.5" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M3 4.5L6 7.5L9 4.5" stroke="#9ca3af" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M4.667 1.167V3.5" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.333 1.167V3.5" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="1.75" y="2.333" width="10.5" height="10.5" rx="1.5" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1.75 5.833H12.25" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3.5 3.5L10.5 10.5" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.5 3.5L3.5 10.5" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon({ color = '#ffffff' }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2.917 7H11.083" stroke={color} strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 2.917V11.083" stroke={color} strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M8.75 3.5L5.25 7L8.75 10.5" stroke="#6b7280" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="#6b7280" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------- Modal icon helpers ---------- */
function ModalCloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M5 5L15 15" stroke="#999999" strokeWidth="1.667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 5L5 15" stroke="#999999" strokeWidth="1.667" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MediatorIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 6.417a2.333 2.333 0 1 0 0-4.667 2.333 2.333 0 0 0 0 4.667Z" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.333 12.25v-1.167a2.333 2.333 0 0 1 2.334-2.333h4.666a2.333 2.333 0 0 1 2.334 2.333v1.167" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FirmIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 1.167v11.666" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1.167 7h5.833" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.5 5.25v7.583" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.833 3.5h2.334" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.833 5.833h2.334" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.833 8.167h2.334" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.833 10.5h2.334" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 8.75a4.083 4.083 0 0 0-4.083 3.5" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="7" cy="4.083" r="2.333" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.167" y="2.333" width="11.667" height="9.333" rx="1.167" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1.167 4.083l5.833 3.5 5.833-3.5" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CaseNameIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M4.667 1.167v10.5" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.333 1.167v2.333" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="1.167" y="3.5" width="11.667" height="8.167" rx="1.167" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CaseTypeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.833" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="4.083" cy="4.083" r="0.292" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NotesIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2.333 1.167h9.334v11.666H2.333z" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.167 1.167v3.5" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.667 5.25h1.166" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.667 7.583h4.666" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.667 9.917h4.666" stroke="#115e59" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CreateInquiryIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <path d="M13.75 1.25L6.82 8.18" stroke="#ffffff" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.75 1.25L9.375 13.75 6.82 8.18 1.25 5.625 13.75 1.25Z" stroke="#ffffff" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ModalChevronDown() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M4 6L8 10L12 6" stroke="#bcbcbc" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------- Log New Inquiry Modal ---------- */
function LogNewInquiryModal({ onClose, onCreated }) {
  const [formData, setFormData] = useState({
    mediatorId: '',
    mediatorName: '',
    requestingFirm: '',
    name: '',
    email: '',
    phone: '',
    caseName: '',
    caseType: '',
    notes: '',
  });
  const [mediators, setMediators] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/mediators?limit=200')
      .then(res => setMediators((res.data?.data || []).filter(m => m.name?.trim())))
      .catch(() => {});
  }, []);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleMediatorChange = (e) => {
    const selected = mediators.find(m => m.userId === e.target.value);
    setFormData(prev => ({
      ...prev,
      mediatorId: e.target.value,
      mediatorName: selected ? selected.name : '',
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.mediatorId) {
      setError('Mediator, Name, and Email are required');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api.post('/inquiries', {
        mediator_id: formData.mediatorId,
        mediator_name: formData.mediatorName,
        requesting_party: formData.name,
        contact_email: formData.email,
        contact_phone: formData.phone,
        firm: formData.requestingFirm,
        case_name: formData.caseName,
        case_type: formData.caseType,
        notes: formData.notes,
        status: 'New',
      });
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    height: 42,
    borderRadius: 8,
    border: '1px solid #e2e2e2',
    backgroundColor: '#ffffff',
    padding: '0 14px',
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    color: '#0d0d0d',
    outline: 'none',
  };

  const labelStyle = {
    fontFamily: 'Inter, sans-serif',
    fontSize: 13,
    fontWeight: 500,
    color: '#0d0d0d',
  };

  const sectionLabelStyle = {
    fontFamily: 'Inter, sans-serif',
    fontSize: 11,
    fontWeight: 600,
    color: '#888888',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 1000, backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
      onClick={onClose}
    >
      <div
        className="flex flex-col"
        style={{
          width: 805,
          maxHeight: '90vh',
          borderRadius: 16,
          backgroundColor: '#fafbfc',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative shrink-0" style={{ height: 100 }}>
          <div style={{ position: 'absolute', left: 32, top: 28, width: 4, height: 45, borderRadius: 2, backgroundColor: '#115e59' }} />
          <div className="flex flex-col" style={{ position: 'absolute', left: 52, top: 28, gap: 4 }}>
            <span style={{ fontFamily: '"Inter Tight", sans-serif', fontSize: 20, fontWeight: 700, color: '#0d0d0d', lineHeight: '24px' }}>
              Log New Inquiry
            </span>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 400, color: '#94949a', lineHeight: '17px' }}>
              Fill in the details to submit a new inquiry
            </span>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center"
            style={{ position: 'absolute', right: 21, top: 29, width: 36, height: 36, borderRadius: 8, backgroundColor: '#f5f5f5', border: 'none', cursor: 'pointer' }}
          >
            <ModalCloseIcon />
          </button>
        </div>

        {/* Form Body */}
        <div className="flex flex-col overflow-y-auto" style={{ padding: '28px 36px', gap: 28 }}>
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 8, backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#dc2626' }}>{error}</span>
            </div>
          )}

          {/* Mediator */}
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={labelStyle}>Mediator <span style={{ color: '#dc2626' }}>*</span></span>
            <div style={{ position: 'relative' }}>
              <select
                value={formData.mediatorId}
                onChange={handleMediatorChange}
                style={{ ...inputStyle, appearance: 'none', color: formData.mediatorId ? '#0d0d0d' : '#bcbcbc' }}
              >
                <option value="" disabled>Select mediator...</option>
                {mediators.map(m => (
                  <option key={m.id} value={m.userId}>{m.name}</option>
                ))}
              </select>
              <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <ModalChevronDown />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="flex flex-col" style={{ gap: 16 }}>
            <div className="flex items-center" style={{ gap: 8 }}>
              <div style={{ width: 16, height: 2, borderRadius: 1, backgroundColor: '#115e59' }} />
              <span style={sectionLabelStyle}>Contact Information</span>
            </div>

            <div className="flex flex-col" style={{ gap: 6 }}>
              <span style={labelStyle}>Requesting Firm</span>
              <input type="text" placeholder="e.g. Littler Mendelson" value={formData.requestingFirm} onChange={handleChange('requestingFirm')} style={inputStyle} />
            </div>

            <div className="flex" style={{ gap: 14 }}>
              <div className="flex flex-col flex-1" style={{ gap: 6 }}>
                <span style={labelStyle}>Name <span style={{ color: '#dc2626' }}>*</span></span>
                <input type="text" placeholder="Full name" value={formData.name} onChange={handleChange('name')} style={inputStyle} />
              </div>
              <div className="flex flex-col flex-1" style={{ gap: 6 }}>
                <span style={labelStyle}>Email <span style={{ color: '#dc2626' }}>*</span></span>
                <input type="email" placeholder="email@example.com" value={formData.email} onChange={handleChange('email')} style={inputStyle} />
              </div>
            </div>

            <div className="flex flex-col" style={{ gap: 6, maxWidth: '50%', paddingRight: 7 }}>
              <span style={labelStyle}>Phone</span>
              <input type="tel" placeholder="(555) 123-4567" value={formData.phone} onChange={handleChange('phone')} style={inputStyle} />
            </div>
          </div>

          {/* Case Details */}
          <div className="flex flex-col" style={{ gap: 16 }}>
            <div className="flex items-center" style={{ gap: 8 }}>
              <div style={{ width: 16, height: 2, borderRadius: 1, backgroundColor: '#115e59' }} />
              <span style={sectionLabelStyle}>Case Details</span>
            </div>

            <div className="flex" style={{ gap: 14 }}>
              <div className="flex flex-col flex-1" style={{ gap: 6 }}>
                <span style={labelStyle}>Case Name</span>
                <input type="text" placeholder="Enter case name" value={formData.caseName} onChange={handleChange('caseName')} style={inputStyle} />
              </div>
              <div className="flex flex-col flex-1" style={{ gap: 6 }}>
                <span style={labelStyle}>Case Type</span>
                <div style={{ position: 'relative' }}>
                  <select
                    value={formData.caseType}
                    onChange={handleChange('caseType')}
                    style={{ ...inputStyle, appearance: 'none', color: formData.caseType ? '#0d0d0d' : '#bcbcbc' }}
                  >
                    <option value="" disabled>Select type...</option>
                    <option value="Employment">Employment</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Personal Injury">Personal Injury</option>
                    <option value="Family">Family</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Construction">Construction</option>
                    <option value="Other">Other</option>
                  </select>
                  <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                    <ModalChevronDown />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col" style={{ gap: 6 }}>
              <span style={labelStyle}>Notes</span>
              <textarea
                placeholder="Special requests, scheduling preferences..."
                value={formData.notes}
                onChange={handleChange('notes')}
                style={{ ...inputStyle, height: 110, padding: '12px 14px', resize: 'none', lineHeight: 1.5 }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end shrink-0" style={{ padding: '20px 32px 28px 32px', gap: 12 }}>
          <button
            onClick={onClose}
            style={{ padding: '10px 22px', borderRadius: 8, border: '1px solid #e2e2e2', backgroundColor: 'transparent', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 500, color: '#888888' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{ padding: '10px 24px', borderRadius: 8, backgroundColor: submitting ? '#5b9e9a' : '#115e59', border: 'none', cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: '#ffffff' }}
          >
            {submitting ? 'Creating...' : 'Create Inquiry'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Inquiries() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showNewInquiryModal, setShowNewInquiryModal] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [mediatorFilter, setMediatorFilter] = useState('');

  // Which row's hold popover is open, and where to anchor it. Kept on the page
  // rather than in the cell so opening one closes any other.
  const [openHoldsFor, setOpenHoldsFor] = useState(null);
  const [holdsAnchor, setHoldsAnchor] = useState(null);

  const { widths: colWidths, setWidth: setColWidth } = useResizableColumns(
    'inquiries.list',
    { inquiryId: 140, mediator: 180, requestingParty: 180, email: 220, firm: 160, status: 130, followUp: 140, onHold: 190 }
  );
  const { sortedRows: sortedInquiries, sort: tableSort, requestSort: requestTableSort } = useSortableData(
    inquiries,
    {
      inquiryId: (r) => r.inquiryId,
      mediator: (r) => r.mediator,
      requestingParty: (r) => r.requestingParty,
      email: (r) => r.email,
      firm: (r) => r.firm,
      status: (r) => r.status,
      followUp: (r) => r.followUpRaw,
      dateSubmitted: (r) => r.dateSubmittedRaw,
      // Sorted by the EARLIEST held date, not the count: the question this
      // column answers is which inquiries have dates coming up, and the
      // backend already returns holds date-ascending. A row with no holds
      // yields '' and the sort hook sinks it to the bottom either direction.
      onHold: (r) => r.holdDates?.[0]?.iso || '',
    }
  );
  // Seed FilterBar from URL search params when navigated from a
  // Dashboard chart click (?startDate=…&endDate=…).
  const [searchParams] = useSearchParams();
  const initialStart = searchParams.get('startDate') || '';
  const initialEnd = searchParams.get('endDate') || '';
  const [dateFilter, setDateFilter] = useState(initialStart && initialEnd ? 'custom' : 'all');
  const [customStart, setCustomStart] = useState(initialStart);
  const [customEnd, setCustomEnd] = useState(initialEnd);
  const [mediatorsList, setMediatorsList] = useState([]);
  const [statusCounts, setStatusCounts] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  // "New" is auto-applied to fresh inquiries; never user-selectable as a
  // filter or status change.  Drop it from the filter options.
  /* 'On Hold' is a DERIVED choice, not a stored status — it asks the API for
   * inquiries holding calendar dates, which is orthogonal to the workflow
   * status and never overwrites it. It sits last so the three real statuses
   * keep their existing order. */
  const STATUS_OPTIONS = ['', 'In Progress', 'Converted', 'Not Converted', 'On Hold'];

  useEffect(() => {
    api.get('/mediators?limit=200')
      .then(res => setMediatorsList((res.data?.data || []).filter(m => m.name?.trim())))
      .catch(console.error);
    // /inquiries/stats now respects the same filters as the list, so
    // re-fetch it on filter change (the dedicated effect below).
  }, []);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, mediatorFilter, dateFilter, customStart, customEnd]);

  // Build the shared filter query string used by both the inquiries list
  // and the /inquiries/stats KPI endpoint. Centralized so the two stay
  // in sync.
  const buildInquiryFilterParams = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (statusFilter) params.set('status', statusFilter);
    if (mediatorFilter) params.set('mediatorId', mediatorFilter);
    const { startDate, endDate } = dateRangeForFilter(dateFilter, customStart, customEnd);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return params;
  };

  const fetchInquiries = () => {
    setLoading(true);
    const params = buildInquiryFilterParams();
    params.set('page', currentPage);
    params.set('limit', pageSize);
    // Re-fetch the KPI stats with the same filters so the tiles narrow
    // along with the table.
    const statsQs = buildInquiryFilterParams().toString();
    api.get(`/inquiries/stats${statsQs ? `?${statsQs}` : ''}`)
      .then(res => setStatusCounts(res.data?.data || null))
      .catch(console.error);
    api.get(`/inquiries?${params.toString()}`)
      .then(res => {
        const mapped = (res.data.data || []).map(inq => ({
          id: inq.id,
          inquiryId: inq.inquiry_number || `INQ-${String(inq.id).padStart(3, '0')}`,
          mediator: inq.mediator_name || 'Unassigned',
          requestedDates: inq.requested_dates || '',
          requestingParty: inq.requesting_party || '',
          email: inq.contact_email || '',
          firm: inq.firm || '',
          status: inq.status === 'New' ? 'In Progress' : (inq.status || 'In Progress'),
          // Submitted date = `created_at` (a full timestamp). Render on the
          // Pacific business calendar so it matches the created_at date the
          // date filter buckets against (and Scale Portal), not browser-local
          // — a late-UTC timestamp would otherwise drift to the next day.
          dateSubmitted: inq.created_at ? new Date(inq.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' }) : '',
          dateSubmittedRaw: inq.created_at || '',
          // Follow-Up = its own bare DATE column. Keep the raw YYYY-MM-DD for
          // sorting (lexical order == chronological) and a formatted label for
          // display. Nulls sink to the bottom via the sort hook.
          followUp: formatShortDatePart(inq.follow_up_date),
          followUpRaw: inq.follow_up_date || '',
          // One entry per held date, already collapsed and sorted by the
          // backend. A half-day hold is labelled Morning/Afternoon in the
          // popover — the column exists so an operator can see which days are
          // spoken for, and a half-held day shown identically to a full one
          // defeats that.
          holdDates: Array.isArray(inq.holds)
            ? inq.holds.map((h) => ({
                iso: h.date,
                isFullDay: !!h.is_full_day,
                slot: (h.slots || [])[0]?.slot || null,
                duration: h.hold_duration || null,
              }))
            : [],
        }));
        setInquiries(mapped);
        setTotalEntries(res.data.total || 0);
        setTotalPages(res.data.pages || 1);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const handler = setTimeout(fetchInquiries, 250);
    return () => clearTimeout(handler);
  }, [currentPage, pageSize, searchQuery, statusFilter, mediatorFilter, dateFilter, customStart, customEnd]);

  const handleRowClick = (row) => {
    navigate(`/inquiries/${row.id}`);
  };

  return (
    <div className="flex flex-col" style={{ minHeight: '100vh' }}>
      {/* Header Bar - 60px */}
      <div
        className="flex items-center justify-between shrink-0"
        style={{ height: 60, padding: '14px 24px', borderBottom: '1px solid #e5e5e5' }}
      >
        <h1
          style={{
            fontFamily: '"Inter Tight", sans-serif',
            fontSize: 14,
            fontWeight: 600,
            color: '#0e1e20',
            lineHeight: 1.5,
          }}
        >
          Inquiry Management
        </h1>
        <button
          className="flex items-center gap-[6px] rounded-lg"
          style={{
            backgroundColor: '#115e59',
            height: 32,
            padding: '0 14px',
            border: 'none',
            cursor: 'pointer',
          }}
          onClick={() => setShowNewInquiryModal(true)}
        >
          <PlusIcon />
          <span
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              fontWeight: 500,
              color: '#ffffff',
            }}
          >
            Log New Inquiry
          </span>
        </button>
      </div>

      {/* Filters (above tiles, drives both /inquiries/stats KPIs and /inquiries list) */}
      <div className="shrink-0" style={{ padding: '20px 24px 0 24px' }}>
        <FilterBar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search inquiries..."
          dateValue={dateFilter}
          onDateChange={setDateFilter}
          dateOptions={DEFAULT_DATE_PRESETS}
          dateDefaultValue="all"
          customStart={customStart}
          customEnd={customEnd}
          onCustomDateChange={(s, e) => { setCustomStart(s); setCustomEnd(e); }}
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          statusOptions={STATUS_OPTIONS.filter(Boolean).map((s) => ({ label: s, value: s }))}
          mediatorValue={mediatorFilter}
          onMediatorChange={setMediatorFilter}
          mediators={mediatorsList}
        />
      </div>

      {/* KPI Metrics */}
      <div
        className="flex gap-3 shrink-0"
        style={{ padding: '20px 24px 26px 24px', borderBottom: '1px solid #e5e5e5' }}
      >
        {(() => {
          // Period chip text — reflects the active date+status filter
          // so the tiles show what slice they're describing.
          const presetLabel = (DEFAULT_DATE_PRESETS.find((p) => p.value === dateFilter) || {}).label;
          const periodLabel =
            dateFilter === 'custom' && (customStart || customEnd)
              ? `${customStart || '…'} – ${customEnd || '…'}`
              : (presetLabel || 'All time');
          return buildKpiCards(loading, totalEntries, statusCounts).map((card, i) => (
          <div
            key={i}
            className="flex flex-col flex-1 rounded-[10px]"
            style={{ border: '1px solid #e5e5e5', backgroundColor: '#ffffff' }}
          >
            {/* Card header */}
            <div
              className="flex items-center gap-2"
              style={{ padding: '12px 14px', borderBottom: '1px solid #e5e5e5' }}
            >
              <span
                style={{
                  fontFamily: '"Inter Tight", sans-serif',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#0e1e20',
                  lineHeight: 1.5,
                }}
              >
                {card.label}
              </span>
              <span
                className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-md whitespace-nowrap"
                style={{ padding: '3px 7px', fontFamily: 'Inter Tight' }}
              >
                {periodLabel}
              </span>
            </div>
            {/* Card body */}
            <div
              className="flex flex-col items-end justify-end"
              style={{ padding: '20px 14px 14px 14px', gap: 14 }}
            >
              <span
                className="self-start"
                style={{
                  fontFamily: '"Inter Tight", sans-serif',
                  fontSize: 30,
                  fontWeight: 500,
                  color: '#0e1e20',
                  lineHeight: 1,
                }}
              >
                {card.value}
              </span>
              <div className="flex items-end">
                <div
                  className="flex items-center justify-center gap-[5px] rounded-full"
                  style={{
                    backgroundColor: card.bgColor,
                    padding: '4px 8px 5px 8px',
                  }}
                >
                  {card.direction === 'up' ? (
                    <TrendUpIcon color={card.changeColor} />
                  ) : (
                    <TrendDownIcon color={card.changeColor} />
                  )}
                  <div className="flex items-center gap-[3px]">
                    <span
                      style={{
                        fontFamily: '"Inter Tight", sans-serif',
                        fontSize: 10,
                        fontWeight: 500,
                        color: card.changeColor,
                      }}
                    >
                      {card.change}
                    </span>
                    <span
                      style={{
                        fontFamily: '"Inter Tight", sans-serif',
                        fontSize: 10,
                        fontWeight: 500,
                        color: '#0e1e20',
                      }}
                    >
                      vs
                    </span>
                    <span
                      style={{
                        fontFamily: '"Inter Tight", sans-serif',
                        fontSize: 10,
                        fontWeight: 500,
                        color: '#0e1e20',
                      }}
                    >
                      {card.compareLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          ));
        })()}
      </div>


      {/* Table Section */}
      <div className="flex-1 relative" style={{ padding: '24px 24px 32px 24px' }}>
        {loading && (
          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-[#5b5a64] font-['Inter']">Loading...</span>
            </div>
          </div>
        )}
        <div className="rounded-lg" style={{ border: '1px solid #e5e5e5', overflow: 'hidden' }}>
          {/* Table Header */}
          <div
            className="flex items-stretch"
            style={{ height: 45, backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e5e5' }}
          >
            <div className="flex items-center min-w-0 relative" style={{ padding: '0 14px', width: colWidths.inquiryId }}>
              <SortableHeader sortKey="inquiryId" sort={tableSort} onSort={requestTableSort} style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: '#5b5a64' }}>Inquiry ID</SortableHeader>
              <ResizeHandle onResize={setColWidth('inquiryId')} getCurrentWidth={() => colWidths.inquiryId} />
            </div>
            <div className="flex items-center min-w-0 relative" style={{ padding: '0 14px', width: colWidths.mediator }}>
              <SortableHeader sortKey="mediator" sort={tableSort} onSort={requestTableSort} style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: '#5b5a64' }}>Mediator</SortableHeader>
              <ResizeHandle onResize={setColWidth('mediator')} getCurrentWidth={() => colWidths.mediator} />
            </div>
            {/* Requested Dates column removed per product spec. */}
            <div className="flex items-center min-w-0 relative" style={{ padding: '0 14px', width: colWidths.requestingParty }}>
              <SortableHeader sortKey="requestingParty" sort={tableSort} onSort={requestTableSort} style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: '#5b5a64' }}>Requesting Party</SortableHeader>
              <ResizeHandle onResize={setColWidth('requestingParty')} getCurrentWidth={() => colWidths.requestingParty} />
            </div>
            <div className="flex items-center min-w-0 relative" style={{ padding: '0 14px', width: colWidths.email }}>
              <SortableHeader sortKey="email" sort={tableSort} onSort={requestTableSort} style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: '#5b5a64' }}>Email</SortableHeader>
              <ResizeHandle onResize={setColWidth('email')} getCurrentWidth={() => colWidths.email} />
            </div>
            <div className="flex items-center min-w-0 relative" style={{ padding: '0 14px', width: colWidths.firm }}>
              <SortableHeader sortKey="firm" sort={tableSort} onSort={requestTableSort} style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: '#5b5a64' }}>Firm</SortableHeader>
              <ResizeHandle onResize={setColWidth('firm')} getCurrentWidth={() => colWidths.firm} />
            </div>
            <div className="flex items-center min-w-0 relative" style={{ padding: '0 14px', width: colWidths.status }}>
              <SortableHeader sortKey="status" sort={tableSort} onSort={requestTableSort} style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: '#5b5a64' }}>Status</SortableHeader>
              <ResizeHandle onResize={setColWidth('status')} getCurrentWidth={() => colWidths.status} />
            </div>
            <div className="flex items-center min-w-0 relative" style={{ padding: '0 14px', width: colWidths.followUp }}>
              <SortableHeader sortKey="followUp" sort={tableSort} onSort={requestTableSort} style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: '#5b5a64' }}>Follow-Up</SortableHeader>
              <ResizeHandle onResize={setColWidth('followUp')} getCurrentWidth={() => colWidths.followUp} />
            </div>
            <div className="flex items-center min-w-0 relative" style={{ padding: '0 14px', width: colWidths.onHold }}>
              <SortableHeader sortKey="onHold" sort={tableSort} onSort={requestTableSort} style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: '#5b5a64' }}>On Hold</SortableHeader>
              <ResizeHandle onResize={setColWidth('onHold')} getCurrentWidth={() => colWidths.onHold} />
            </div>
            <div className="flex-1 flex items-center min-w-0" style={{ padding: '0 14px' }}>
              <SortableHeader sortKey="dateSubmitted" sort={tableSort} onSort={requestTableSort} style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600, color: '#5b5a64' }}>Date Submitted</SortableHeader>
            </div>
          </div>

          {/* Table Rows */}
          {!loading && inquiries.length === 0 && (
            <div className="flex items-center justify-center" style={{ height: 120 }}>
              <span className="text-sm text-[#5b5a64] font-['Inter']">No inquiries found</span>
            </div>
          )}
          {sortedInquiries.map((row) => (
            <div
              key={row.id}
              className="flex items-stretch cursor-pointer hover:bg-gray-50 transition-colors"
              style={{ height: 48, borderBottom: '1px solid #e5e5e5' }}
              onClick={() => handleRowClick(row)}
            >
              <div className="flex items-center min-w-0" style={{ padding: '0 14px', width: colWidths.inquiryId }}>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 500, color: '#0e1e20' }}>
                  {row.inquiryId}
                </span>
              </div>
              <div className="flex items-center min-w-0" style={{ padding: '0 14px', width: colWidths.mediator }}>
                <span className="truncate" style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 400, color: '#0e1e20' }}>
                  {row.mediator}
                </span>
              </div>
              {/* Requested Dates cell removed alongside the column header. */}
              <div className="flex items-center min-w-0" style={{ padding: '0 14px', width: colWidths.requestingParty }}>
                <span className="truncate" style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 400, color: '#0e1e20' }}>
                  {row.requestingParty}
                </span>
              </div>
              <div className="flex items-center min-w-0" style={{ padding: '0 14px', width: colWidths.email }}>
                <span className="truncate" style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 400, color: '#0e1e20' }}>
                  {row.email}
                </span>
              </div>
              <div className="flex items-center min-w-0" style={{ padding: '0 14px', width: colWidths.firm }}>
                <span className="truncate" style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 400, color: '#0e1e20' }}>
                  {row.firm}
                </span>
              </div>
              <div className="flex items-center min-w-0" style={{ padding: '0 14px', width: colWidths.status }}>
                {getStatusBadge(row.status)}
              </div>
              <div className="flex items-center min-w-0" style={{ padding: '0 14px', width: colWidths.followUp }}>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 400, color: row.followUp ? '#5b5a64' : '#9ca3af' }}>
                  {row.followUp || '—'}
                </span>
              </div>
              <div className="flex items-center min-w-0" style={{ padding: '0 14px', width: colWidths.onHold }}>
                <HoldDatesCell
                  holds={row.holdDates}
                  firm={row.firm}
                  open={openHoldsFor === row.id}
                  onToggle={(rect) => {
                    setHoldsAnchor(rect);
                    setOpenHoldsFor(openHoldsFor === row.id ? null : row.id);
                  }}
                  onClose={() => setOpenHoldsFor(null)}
                  anchor={holdsAnchor}
                  onOpenInquiry={() => navigate(`/inquiries/${row.id}`)}
                />
              </div>
              <div className="flex-1 flex items-center min-w-0" style={{ padding: '0 14px' }}>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 400, color: '#5b5a64' }}>
                  {row.dateSubmitted}
                </span>
              </div>
            </div>
          ))}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalEntries={totalEntries}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      </div>

      {/* Log New Inquiry Modal */}
      {showNewInquiryModal && (
        <LogNewInquiryModal
          onClose={() => setShowNewInquiryModal(false)}
          onCreated={() => {
            fetchInquiries();
            api.get('/inquiries/stats').then(res => setStatusCounts(res.data?.data || null)).catch(() => {});
          }}
        />
      )}
    </div>
  );
}
